/**
 * useVoiceMemoRecorder — Streaming voice-memo recorder for the Notes & Files
 * (Cockpit Data Vault) voice-input feature.
 *
 * CORE DESIGN (no data loss under any circumstance)
 * -------------------------------------------------
 *  - We record with the browser's MediaRecorder API in ~12-second CYCLES:
 *    start → wait 12s → stop → get a self-contained WebM/Opus blob → start
 *    a fresh recorder. This differs from the more common `timeslice` mode,
 *    whose fragments are NOT independently decodable and would require
 *    server-side stitching (which then also becomes a single point of
 *    failure). Cycling gives us fully-formed audio files that Whisper
 *    accepts as-is; the ~30–80 ms gap between cycles is inaudible in
 *    normal speech.
 *  - Every complete chunk is uploaded IMMEDIATELY to Firebase Storage at
 *    World/${userId}/dataVault/voiceMemos/${memoId}/chunk_${idx}.webm.
 *    Once the upload resolves, the chunk is durable — even a full browser
 *    crash cannot lose it.
 *  - After each upload we call the `transcribeVoiceMemoChunk` cloud function,
 *    which writes the resulting text to RTDB at
 *    voiceMemos/${memoId}/segments/${idx}. The UI subscribes to that node
 *    and shows the running transcript live.
 *  - If the transcription call itself fails, the RAW AUDIO is still in
 *    Storage — the app can retry that specific chunk later without asking
 *    the user to re-speak.
 *
 * Public API
 * ----------
 *   const rec = useVoiceMemoRecorder({ userId, currentUserId, canEdit });
 *   rec.state          // 'idle' | 'requesting' | 'recording' | 'paused' | 'stopping' | 'error'
 *   rec.memoId         // string | null (assigned on start; persistent for recovery)
 *   rec.elapsedMs      // total wall-clock recording duration
 *   rec.level          // 0..1 live RMS of the mic input (for a waveform meter)
 *   rec.chunkStats     // { total, uploaded, transcribed, failed }
 *   rec.error          // last error string (permission, encoder, network)
 *   rec.mimeType       // negotiated MIME (for display/debug)
 *   rec.start({ language })    // opens mic, creates memo id, begins cycling
 *   rec.pause()                // stops mic + AudioContext (keeps memoId)
 *   rec.resume()               // resumes recording into the same memo
 *   rec.stop()                 // finalises: flushes last chunk, uploads, transcribes
 *   rec.cancel()               // aborts: marks memo cancelled, releases mic
 *   rec.reset()                // clears state so a fresh memo can start
 *
 * Notes on lifecycle
 *   The hook DOES NOT trigger the AI structuring step — that's the modal's
 *   responsibility (called after stop() has settled and the segments are
 *   done). The hook is purely about safe capture + live transcription.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ref as dbRef,
  push,
  set,
  update,
  serverTimestamp,
} from 'firebase/database';
import {
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import { database, storage } from '../firebaseConfig';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';

// Rotate the recorder every this many ms. Trade-off:
//   - Shorter → snappier live transcript, more HTTP calls, more $$.
//   - Longer → cheaper, but the "latest sentence" shows up later.
// 12s felt right in testing: 3 sentences of average speech per chunk,
// perceived latency < 15 s from mouth to screen.
const CHUNK_MS = 12000;

// Absolute cap on a single memo. Whisper still works past this, but the UX
// becomes cumbersome and cost rises linearly. Twenty minutes covers even
// long brain-dump sessions.
const MAX_MEMO_MS = 20 * 60 * 1000;

// Level meter refresh rate (fps). 30 fps is smooth and cheap.
const LEVEL_FPS = 30;

/**
 * Pick the best MediaRecorder mimeType this browser supports.
 * Ordered by size/quality trade-off. Whisper accepts all of them via its
 * mimetype sniffing, so we just take the first one the browser can produce.
 */
function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
  ];
  for (const t of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(t)) return t;
    } catch (_) { /* Safari throws for some strings */ }
  }
  return ''; // let the browser pick its own default
}

function extensionFromMime(mime) {
  if (!mime) return 'webm';
  if (mime.startsWith('audio/webm')) return 'webm';
  if (mime.startsWith('audio/ogg')) return 'ogg';
  if (mime.startsWith('audio/mp4')) return 'm4a';
  if (mime.startsWith('audio/wav') || mime.startsWith('audio/wave')) return 'wav';
  return 'webm';
}

/**
 * Format a MediaRecorder error into something a human can read.
 */
function friendlyMediaError(e) {
  const name = e?.name || '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Microphone permission was denied. Enable it in your browser settings and try again.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'No microphone was detected. Plug one in and try again.';
  }
  if (name === 'NotReadableError') {
    return 'The microphone is being used by another app. Close it and try again.';
  }
  if (name === 'AbortError') {
    return 'Recording was aborted by the browser.';
  }
  if (name === 'TimeoutError') {
    return 'The browser is still waiting for microphone permission. Allow access in the prompt, then try again.';
  }
  return e?.message || 'Could not access the microphone.';
}

const GET_USER_MEDIA_MS = 45000;

/**
 * Ask for the mic with the most compatible constraint set.
 * `sampleRate` / `channelCount` overconstraints hang or fail on first
 * grant on some devices (Safari, USB mics, Bluetooth headsets).
 *
 * Do not pass AbortSignal into getUserMedia constraints — that is not a
 * standard field and can throw on Safari. If we time out or cancel first,
 * stop any stream that arrives later so the mic does not stay hot.
 */
function requestMicStream(abortSignal) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (abortSignal) abortSignal.removeEventListener('abort', onAbort);
      fn();
    };
    const onAbort = () => {
      finish(() => {
        const err = new Error('Recording was aborted.');
        err.name = 'AbortError';
        reject(err);
      });
    };
    const timer = setTimeout(() => {
      finish(() => {
        const err = new Error('Microphone permission timed out.');
        err.name = 'TimeoutError';
        reject(err);
      });
    }, GET_USER_MEDIA_MS);

    if (abortSignal) {
      if (abortSignal.aborted) {
        onAbort();
        return;
      }
      abortSignal.addEventListener('abort', onAbort);
    }

    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      if (settled) {
        try { stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
        return;
      }
      finish(() => resolve(stream));
    }).catch((e) => {
      finish(() => reject(e));
    });
  });
}

export default function useVoiceMemoRecorder({ userId, currentUserId, canEdit }) {
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  const [memoId, setMemoId] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState(0);
  const [mimeType, setMimeType] = useState(null);
  const [chunkStats, setChunkStats] = useState({
    total: 0, uploaded: 0, transcribed: 0, failed: 0,
  });

  // Refs for anything we DON'T want to trigger re-renders on. Everything
  // MediaRecorder-related is in a ref so cycles don't provoke React work.
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const cycleTimerRef = useRef(null);
  const chunkIdxRef = useRef(0);
  const memoIdRef = useRef(null);
  const languageRef = useRef(null);
  const startedAtRef = useRef(0);
  const totalElapsedRef = useRef(0);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const levelRafRef = useRef(null);
  const stopResolverRef = useRef(null);
  const stateRef = useRef('idle');
  const mountedRef = useRef(true);
  const activeUploadsRef = useRef(0);
  const cancelledRef = useRef(false);
  const micAbortRef = useRef(null);

  // Keep the sync ref up-to-date so timers and async callbacks always see
  // the latest state without re-subscribing.
  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => () => {
    mountedRef.current = false;
    cancelledRef.current = true;
    // Best-effort teardown on unmount so we never leave a hot mic.
    try { hardCleanup(); } catch (_) { /* noop */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const safeSet = useCallback((fn) => {
    if (mountedRef.current) fn();
  }, []);

  const incStat = useCallback((patch) => {
    if (!mountedRef.current) return;
    setChunkStats((prev) => ({
      total: (patch.total ?? 0) + prev.total,
      uploaded: (patch.uploaded ?? 0) + prev.uploaded,
      transcribed: (patch.transcribed ?? 0) + prev.transcribed,
      failed: (patch.failed ?? 0) + prev.failed,
    }));
  }, []);

  const hardCleanup = useCallback(() => {
    if (micAbortRef.current) {
      try { micAbortRef.current.abort(); } catch (_) { /* noop */ }
      micAbortRef.current = null;
    }
    if (cycleTimerRef.current) {
      clearTimeout(cycleTimerRef.current);
      cycleTimerRef.current = null;
    }
    if (levelRafRef.current) {
      cancelAnimationFrame(levelRafRef.current);
      levelRafRef.current = null;
    }
    if (recorderRef.current) {
      try {
        if (recorderRef.current.state !== 'inactive') {
          recorderRef.current.stop();
        }
      } catch (_) { /* noop */ }
      recorderRef.current.ondataavailable = null;
      recorderRef.current.onstop = null;
      recorderRef.current.onerror = null;
      recorderRef.current = null;
    }
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => t.stop());
      } catch (_) { /* noop */ }
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) { /* noop */ }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
  }, []);

  /**
   * Upload a chunk blob → Storage → call the transcribe function.
   * Runs OUTSIDE the recorder cycle so the cycle stays punctual.
   */
  const processChunkBlob = useCallback(async (blob, idx, chunkMs, chunkMime) => {
    if (!blob || blob.size === 0) {
      // Nothing to upload — recorder produced an empty flush (e.g. paused
      // before the very first sample). Skip silently.
      return;
    }
    activeUploadsRef.current += 1;
    incStat({ total: 1 });
    const currentMemoId = memoIdRef.current;
    if (!currentMemoId) {
      activeUploadsRef.current -= 1;
      return;
    }
    const idxStr = String(idx).padStart(4, '0');
    const ext = extensionFromMime(chunkMime);
    const path = `World/${userId}/dataVault/voiceMemos/${currentMemoId}/chunk_${idxStr}.${ext}`;
    try {
      // 1. Upload to Storage (client SDK, respects existing storage.rules).
      await uploadBytes(storageRef(storage, path), blob, {
        contentType: chunkMime || 'audio/webm',
        customMetadata: { memoId: currentMemoId, chunkIndex: String(idx) },
      });
      incStat({ uploaded: 1 });

      // 2. Ask the backend to transcribe. The backend writes the resulting
      //    text to RTDB itself, so we don't need to relay it here.
      try {
        const resp = await authenticatedFetch(CLOUD_FUNCTION_URLS.transcribeVoiceMemoChunk(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            memoId: currentMemoId,
            chunkIndex: idx,
            storagePath: path,
            durationMs: chunkMs,
            language: languageRef.current || undefined,
            mimeType: chunkMime || 'audio/webm',
          }),
        });
        if (!resp.ok) {
          const body = await resp.text().catch(() => '');
          throw new Error(`Transcription HTTP ${resp.status}: ${body.slice(0, 180)}`);
        }
        incStat({ transcribed: 1 });
      } catch (e) {
        // Mark this chunk's segment as failed so the UI can offer a retry.
        console.warn('[voiceMemo] transcription call failed', e.message);
        try {
          await set(
            dbRef(database, `World/${userId}/dataVault/voiceMemos/${currentMemoId}/segments/${idx}`),
            {
              text: '',
              storagePath: path,
              status: 'failed',
              error: (e.message || 'transcription failed').slice(0, 200),
              failedAt: new Date().toISOString(),
            }
          );
        } catch (_) { /* best-effort */ }
        incStat({ failed: 1 });
      }
    } catch (e) {
      console.warn('[voiceMemo] chunk upload failed', e.message);
      incStat({ failed: 1 });
      // Note: we DON'T bail the whole recording. Later chunks keep flowing.
      // The user sees a red badge for the failed chunk in the transcript.
      try {
        await set(
          dbRef(database, `World/${userId}/dataVault/voiceMemos/${currentMemoId}/segments/${idx}`),
          {
            text: '',
            status: 'failed',
            error: (e.message || 'upload failed').slice(0, 200),
            failedAt: new Date().toISOString(),
          }
        );
      } catch (_) { /* best-effort */ }
    } finally {
      activeUploadsRef.current -= 1;
      // Resolve stop() only when the recorder has finished AND all in-flight
      // uploads have drained. This gives the modal a reliable "we're really
      // done" signal to kick off the AI structuring step.
      if (stopResolverRef.current && activeUploadsRef.current === 0 && stateRef.current === 'stopping') {
        const r = stopResolverRef.current;
        stopResolverRef.current = null;
        r();
      }
    }
  }, [userId, incStat]);

  /**
   * Build a fresh MediaRecorder instance bound to the current stream. Wires
   * ondataavailable → uploadChunk. Called at the start of every 12-second
   * cycle.
   */
  const buildRecorder = useCallback((chunkStartedAt) => {
    if (!streamRef.current) return null;
    const mime = mimeType || pickMimeType();
    const options = mime ? { mimeType: mime, audioBitsPerSecond: 96000 } : {};
    let rec;
    try {
      rec = new MediaRecorder(streamRef.current, options);
    } catch (_) {
      // Fallback: some browsers reject the negotiated mimeType — retry
      // without options and let the browser choose.
      try {
        rec = new MediaRecorder(streamRef.current);
      } catch (e) {
        setError(friendlyMediaError(e));
        setState('error');
        return null;
      }
    }
    const chunkParts = [];
    let ownedIdx = null;
    rec.ondataavailable = (evt) => {
      if (evt.data && evt.data.size > 0) chunkParts.push(evt.data);
    };
    rec.onerror = (evt) => {
      console.warn('[voiceMemo] recorder error', evt.error);
    };
    rec.onstop = () => {
      if (ownedIdx === null) return;
      const chunkMime = rec.mimeType || mime || 'audio/webm';
      const blob = chunkParts.length ? new Blob(chunkParts, { type: chunkMime }) : null;
      const chunkMs = Date.now() - chunkStartedAt;
      // Fire-and-forget: uploads run in parallel with the next cycle.
      processChunkBlob(blob, ownedIdx, chunkMs, chunkMime);
    };
    // Attach owned index at start-time so we can preserve ordering even
    // when a stopped recorder finishes fractionally after the next one
    // starts.
    return {
      recorder: rec,
      claim(idx) { ownedIdx = idx; },
    };
  }, [mimeType, processChunkBlob]);

  /**
   * The heart of the cycler. Called after every stop() and also on the
   * initial start(). Grabs the next chunk index, starts a recorder, schedules
   * its stop() in CHUNK_MS.
   */
  const openNextCycle = useCallback(() => {
    if (stateRef.current !== 'recording') return;
    if (totalElapsedRef.current >= MAX_MEMO_MS) {
      // Auto-stop at hard limit — bubble through the normal stop() path so
      // segments + memo status get finalised properly.
      // eslint-disable-next-line no-use-before-define
      finalizeStop();
      return;
    }
    const chunkStartedAt = Date.now();
    const wrapper = buildRecorder(chunkStartedAt);
    if (!wrapper) return;
    const idx = chunkIdxRef.current;
    chunkIdxRef.current = idx + 1;
    wrapper.claim(idx);
    recorderRef.current = wrapper.recorder;
    try {
      wrapper.recorder.start();
    } catch (e) {
      setError(friendlyMediaError(e));
      setState('error');
      return;
    }
    // Schedule the cycle rotation. Slightly randomised to avoid systematic
    // sync with upload latency spikes.
    const dur = CHUNK_MS + Math.floor(Math.random() * 200);
    cycleTimerRef.current = setTimeout(() => {
      cycleTimerRef.current = null;
      if (stateRef.current !== 'recording') return;
      const outgoing = recorderRef.current;
      if (outgoing && outgoing.state !== 'inactive') {
        // Stopping produces the ondataavailable → onstop pair that ships
        // the blob to processChunkBlob. Then we immediately open a fresh
        // cycle so mic capture continues (~30-80ms gap between cycles).
        try { outgoing.stop(); } catch (_) { /* noop */ }
      }
      totalElapsedRef.current += Date.now() - chunkStartedAt;
      openNextCycle();
    }, dur);
  }, [buildRecorder]);

  /**
   * Called when the user hits Stop OR the memo hits its hard limit.
   */
  const finalizeStop = useCallback(async () => {
    if (stateRef.current !== 'recording' && stateRef.current !== 'paused') return;
    setState('stopping');
    stateRef.current = 'stopping';

    if (cycleTimerRef.current) {
      clearTimeout(cycleTimerRef.current);
      cycleTimerRef.current = null;
    }
    // Stop the level meter to save CPU while we wait for uploads to drain.
    if (levelRafRef.current) {
      cancelAnimationFrame(levelRafRef.current);
      levelRafRef.current = null;
    }
    setLevel(0);

    // Flush the current recorder to disk. Its onstop → processChunkBlob will
    // push the final blob through the upload pipeline.
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try { rec.stop(); } catch (_) { /* noop */ }
    }
    recorderRef.current = null;

    // Release mic hardware NOW — the user is done speaking, no reason to
    // keep the mic hot while uploads run.
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { await audioCtxRef.current.close(); } catch (_) { /* noop */ }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;

    const currentMemoId = memoIdRef.current;
    if (currentMemoId) {
      try {
        await update(
          dbRef(database, `World/${userId}/dataVault/voiceMemos/${currentMemoId}`),
          {
            status: 'awaiting-transcription',
            totalElapsedMs: totalElapsedRef.current,
            stoppedAt: new Date().toISOString(),
          }
        );
      } catch (_) { /* best-effort */ }
    }

    // Wait for all in-flight uploads + transcription calls to drain. If
    // there aren't any (nothing was recorded), resolve immediately.
    await new Promise((resolve) => {
      if (activeUploadsRef.current === 0) return resolve();
      stopResolverRef.current = resolve;
      // Safety net: don't wait forever if a transcription hangs. 60s cap.
      setTimeout(() => {
        if (stopResolverRef.current === resolve) {
          stopResolverRef.current = null;
          resolve();
        }
      }, 60000);
    });

    if (currentMemoId) {
      try {
        await update(
          dbRef(database, `World/${userId}/dataVault/voiceMemos/${currentMemoId}`),
          {
            status: 'transcribed',
            transcribedAt: new Date().toISOString(),
          }
        );
      } catch (_) { /* best-effort */ }
    }
    setState('stopped');
    stateRef.current = 'stopped';
  }, [userId]);

  /**
   * Live level meter using an AnalyserNode. Runs at LEVEL_FPS via rAF.
   */
  const startLevelMeter = useCallback(() => {
    if (!streamRef.current) return;
    try {
      const ACtx = window.AudioContext || window.webkitAudioContext;
      if (!ACtx) return;
      const ctx = new ACtx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(streamRef.current);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.4;
      src.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.fftSize);
      const frameInterval = 1000 / LEVEL_FPS;
      let lastFrame = 0;
      const tick = (ts) => {
        levelRafRef.current = requestAnimationFrame(tick);
        if (ts - lastFrame < frameInterval) return;
        lastFrame = ts;
        analyser.getByteTimeDomainData(buf);
        // RMS of the centered [-1,1] wave.
        let sumSq = 0;
        for (let i = 0; i < buf.length; i += 1) {
          const v = (buf[i] - 128) / 128;
          sumSq += v * v;
        }
        const rms = Math.sqrt(sumSq / buf.length);
        // Perceptual boost so quiet speech shows on the meter.
        const shaped = Math.min(1, Math.pow(rms * 3, 0.7));
        if (mountedRef.current) setLevel(shaped);
      };
      levelRafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      console.warn('[voiceMemo] level meter unavailable', e.message);
    }
  }, []);

  /**
   * Elapsed-time tick, updated every 250 ms while recording. Cheap — one
   * setState per second at most (we round for display anyway).
   */
  useEffect(() => {
    if (state !== 'recording') return undefined;
    startedAtRef.current = Date.now() - totalElapsedRef.current;
    const t = setInterval(() => {
      if (!mountedRef.current) return;
      const el = Date.now() - startedAtRef.current;
      setElapsedMs(el);
    }, 250);
    return () => clearInterval(t);
  }, [state]);

  const start = useCallback(async ({ language } = {}) => {
    if (!canEdit) {
      setError('You do not have permission to record voice memos here.');
      setState('error');
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('Voice recording is not supported in this browser.');
      setState('error');
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      setError('This browser does not support in-browser audio recording.');
      setState('error');
      return;
    }
    if (stateRef.current === 'requesting' || stateRef.current === 'recording') {
      return;
    }
    setError(null);
    setState('requesting');
    stateRef.current = 'requesting';
    languageRef.current = language || null;
    cancelledRef.current = false;
    totalElapsedRef.current = 0;
    setElapsedMs(0);
    chunkIdxRef.current = 0;
    setChunkStats({ total: 0, uploaded: 0, transcribed: 0, failed: 0 });

    const abortCtrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    micAbortRef.current = abortCtrl;

    let stream;
    try {
      stream = await requestMicStream(abortCtrl?.signal);
    } catch (e) {
      micAbortRef.current = null;
      if (cancelledRef.current || e?.name === 'AbortError') {
        setState('idle');
        stateRef.current = 'idle';
        return;
      }
      setError(friendlyMediaError(e));
      setState('error');
      stateRef.current = 'error';
      return;
    }
    micAbortRef.current = null;

    if (cancelledRef.current) {
      try { stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
      setState('idle');
      stateRef.current = 'idle';
      return;
    }

    // First-grant on a new device: wait a tick so tracks are live before
    // MediaRecorder.start(), which otherwise can hang silently.
    await new Promise((r) => setTimeout(r, 60));
    if (cancelledRef.current || !mountedRef.current) {
      try { stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
      if (mountedRef.current) {
        setState('idle');
        stateRef.current = 'idle';
      }
      return;
    }

    streamRef.current = stream;

    // Create a memo record so segments (which the backend will push) have a
    // parent. push() gives us a unique id for the storage path.
    const newRef = push(dbRef(database, `World/${userId}/dataVault/voiceMemos`));
    const newMemoId = newRef.key;
    memoIdRef.current = newMemoId;
    setMemoId(newMemoId);

    const chosenMime = pickMimeType();
    setMimeType(chosenMime);

    try {
      await set(newRef, {
        status: 'recording',
        language: language || null,
        mimeType: chosenMime || null,
        createdAt: new Date().toISOString(),
        createdAtServer: serverTimestamp(),
        createdBy: currentUserId || null,
        chunkMs: CHUNK_MS,
      });
    } catch (e) {
      console.warn('[voiceMemo] failed to create memo record', e.message);
      // Not fatal — segments will still be uploaded; but the memo won't
      // appear in "recent recordings". We surface the error so the modal
      // can decide whether to retry.
      setError(e.message || 'Could not create memo record');
    }

    setState('recording');
    stateRef.current = 'recording';
    startedAtRef.current = Date.now();
    startLevelMeter();
    openNextCycle();
  }, [canEdit, userId, currentUserId, openNextCycle, startLevelMeter]);

  const stop = useCallback(async () => {
    await finalizeStop();
  }, [finalizeStop]);

  const cancel = useCallback(async () => {
    cancelledRef.current = true;
    if (micAbortRef.current) {
      try { micAbortRef.current.abort(); } catch (_) { /* noop */ }
      micAbortRef.current = null;
    }
    if (cycleTimerRef.current) { clearTimeout(cycleTimerRef.current); cycleTimerRef.current = null; }
    hardCleanup();
    const id = memoIdRef.current;
    if (id) {
      try {
        await update(dbRef(database, `World/${userId}/dataVault/voiceMemos/${id}`), {
          status: 'cancelled',
          cancelledAt: new Date().toISOString(),
        });
      } catch (_) { /* noop */ }
    }
    stateRef.current = 'cancelled';
    setState('cancelled');
    setLevel(0);
  }, [hardCleanup, userId]);

  const reset = useCallback(() => {
    hardCleanup();
    memoIdRef.current = null;
    chunkIdxRef.current = 0;
    totalElapsedRef.current = 0;
    setMemoId(null);
    setElapsedMs(0);
    setLevel(0);
    setChunkStats({ total: 0, uploaded: 0, transcribed: 0, failed: 0 });
    setError(null);
    stateRef.current = 'idle';
    setState('idle');
  }, [hardCleanup]);

  return {
    state,
    error,
    memoId,
    elapsedMs,
    level,
    mimeType,
    chunkStats,
    start,
    stop,
    cancel,
    reset,
    // Purely informational — useful for showing a "your browser can only
    // record N minutes total per memo" hint if we ever want it.
    maxMemoMs: MAX_MEMO_MS,
    chunkMs: CHUNK_MS,
  };
}
